import { AsyncPipe, DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
  Signal,
  WritableSignal,
} from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { debounceTime, filter, Observable, take, tap } from 'rxjs';
import {
  ButtonComponent,
  IPaginationEvent,
  ModalService,
  PaginationComponent,
  SvgIconComponent,
  TableColumnItem,
  TableComponent,
  MultiselectComponent,
  SkeletonDirective,
} from '@ui-lib/components';
import { getUsersTableColumnsConfig } from './users-table-columns.config';
import { UsersService } from './services/users.service';

import { IRoleOption, IUserModalDataInterface, UserAction, UserStatusEnum } from './model';
import { SearchUserResponseItem } from '../../core/openapi/bff-admin-api';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { UserModalComponent } from './components/user-modal/user-modal.component';
import { LocalStorageService } from '../../core/services/storage/local-storage.service';
import { LoadingState } from '../../shared/types/loading.type';
import { IDictionaryItem } from '../../shared/types/dictionary-item.interface';
import { PermissionService } from '../../core/services/user/permission.service';
import { UserPermissionEnum } from '../../core/models/user-permissions.enum';
import { PageToolbarComponent } from '../../shared/components/page-toolbar/page-toolbar.component';
import { PageToolbarAction } from '../../shared/components/page-toolbar/page-toolbar.model';

const USER_LIST_FILTER = 'user_list_filter';

interface IUsersStoredFilter {
  keyword: string;
  roleIds: string[];
  rolesInfo?: Array<{ id: string; name?: string }>;
}

@Component({
  standalone: true,
  selector: 'mon-users',
  templateUrl: './users.component.html',
  styleUrls: ['./users.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [UsersService],
  imports: [
    TranslateModule,
    ReactiveFormsModule,
    ButtonComponent,
    AsyncPipe,
    TableComponent,
    PaginationComponent,
    DatePipe,
    SvgIconComponent,
    FormsModule,
    MultiselectComponent,
    SkeletonDirective,
    PageToolbarComponent,
  ],
})
export class UsersComponent implements OnInit {
  private readonly modalService = inject(ModalService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly permissionService = inject(PermissionService);

  protected readonly usersService = inject(UsersService);

  protected readonly UserAction = UserAction;
  protected readonly LoadingState = LoadingState;

  protected readonly columns: TableColumnItem[] = getUsersTableColumnsConfig();

  protected readonly filterForm = new FormGroup({
    keyword: new FormControl<string>('', { nonNullable: true }),
    roles: new FormControl<string[]>([], { nonNullable: true }),
  });

  protected readonly currentUserPage: WritableSignal<number> = signal(1);

  protected readonly UserStatusEnum = UserStatusEnum;
  protected readonly canManageUsers = toSignal(
    this.permissionService.hasPermission(UserPermissionEnum.UserManage),
    { initialValue: false },
  );
  protected readonly canBlockUsers = toSignal(
    this.permissionService.hasPermission(UserPermissionEnum.UserBlock),
    { initialValue: false },
  );

  private readonly allKnownRoles: Map<string, IRoleOption<string>> = new Map();

  protected readonly users$: Observable<SearchUserResponseItem[]> = this.usersService.users$;

  protected readonly existingRoles: Signal<IDictionaryItem<string>[] | undefined> = toSignal<
    IDictionaryItem<string>[]
  >(
    this.usersService.existingRoles$.pipe(
      tap((roles: IDictionaryItem<string>[]): void => {
        roles.forEach((role) => {
          this.allKnownRoles.set(role.id, {
            id: role.id,
            label: role.name,
          });
        });
      }),
    ),
  );

  public ngOnInit(): void {
    const savedFilters = this.getSavedFilters();
    const savedRoleIds = savedFilters.roleIds;

    if (savedFilters.rolesInfo) {
      savedFilters.rolesInfo.forEach((roleInfo): void => {
        if (!this.allKnownRoles.has(roleInfo.id)) {
          this.allKnownRoles.set(roleInfo.id, {
            id: roleInfo.id,
            label: roleInfo.name || `Role ${roleInfo.id}`,
          } as IRoleOption<string>);
        }
      });
    }

    this.searchControl.setValue(savedFilters.keyword, { emitEvent: false });
    this.rolesControl.setValue(savedRoleIds, { emitEvent: false });

    if (savedFilters.keyword || savedRoleIds.length > 0) {
      this.usersService.triggerUsersUpdate({
        keyword: savedFilters.keyword,
        roleIds: savedRoleIds,
        skip: 0,
      });
    }

    this.filterForm.valueChanges
      .pipe(debounceTime(300), takeUntilDestroyed(this.destroyRef))
      .subscribe((changes): void => {
        const keyword: string = changes.keyword ?? '';
        const roleIds = changes.roles ?? [];

        this.usersService.triggerUsersUpdate({ skip: 0, keyword, roleIds });
        this.currentUserPage.set(1);

        this.saveFilters(keyword, roleIds);
      });
  }

  public get isFiltersActive(): boolean {
    return !!(this.searchControl.value.length || this.rolesControl.value.length > 0);
  }

  public get selectedFiltersCount(): number {
    return this.selectedRoles.length;
  }

  public get searchControl(): FormControl<string> {
    return this.filterForm.get<'keyword'>('keyword') as FormControl;
  }

  public get rolesControl(): FormControl<string[]> {
    return this.filterForm.get<'roles'>('roles') as FormControl<string[]>;
  }

  public get selectedRoles(): IRoleOption<string>[] {
    return this.rolesControl.value
      .map((id) => this.allKnownRoles.get(id))
      .filter((role): role is IRoleOption<string> => role !== undefined);
  }

  protected get toolbarActions(): PageToolbarAction[] {
    return this.canManageUsers()
      ? [
          {
            id: 'create',
            icon: 'plus',
            titleTranslateKey: 'monitoring.users.dashboard.add-user',
          },
        ]
      : [];
  }

  protected onToolbarAction(actionId: string): void {
    if (actionId === 'create') {
      this.openUserModal('monitoring.users.modal.user-creation', UserAction.Create);
    }
  }

  private getSavedFilters(): IUsersStoredFilter {
    const source = this.localStorageService.get(USER_LIST_FILTER);

    if (source) {
      try {
        return JSON.parse(source);
      } catch (e) {
        console.error(e);
      }
    }

    return {
      keyword: '',
      roleIds: [],
      rolesInfo: [],
    };
  }

  private saveFilters(keyword: string, roleIds: string[]): void {
    const rolesInfo = roleIds.map((id) => {
      const role = this.allKnownRoles.get(id);
      return {
        id,
        name: role?.label,
      };
    });

    this.localStorageService.set<IUsersStoredFilter>(USER_LIST_FILTER, {
      keyword,
      roleIds,
      rolesInfo,
    });
  }

  public removeRole(role: IRoleOption<string>): void {
    this.rolesControl.setValue(this.rolesControl.value.filter((id) => id !== role.id));
  }

  public clearAllRoles(triggerUpdate = true): void {
    this.rolesControl.setValue([], { emitEvent: triggerUpdate });
  }

  public clearAllFilters(): void {
    this.clearAllRoles(false);
    this.searchControl.setValue('');
  }

  public onPageChange(paginator: IPaginationEvent): void {
    this.currentUserPage.set(paginator.pageIndex);
    this.usersService.triggerUsersUpdate({
      skip: (paginator.pageIndex - 1) * paginator.pageSize,
      take: paginator.pageSize,
    });
  }

  public openUserModal(
    title: string,
    action: UserAction,
    userId?: string,
    roleName?: string,
  ): void {
    if (action === UserAction.Create && !this.canManageUsers()) return;
    if (action === UserAction.Edit && !this.canManageUsers() && !this.canBlockUsers()) return;

    this.modalService
      .open<IUserModalDataInterface>({
        component: UserModalComponent,
        data: {
          title,
          action,
          userId,
          roleName,
        },
        customContainerClass: 'user-modal',
      })
      .afterClose$.pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
        filter((result) => !!result),
      )
      .subscribe(() => {
        this.usersService.triggerUsersUpdate({});
      });
  }
}
